/*
     EXAMPLE USAGE:
      <Fields
         names={["fromDate", "toRange"]}
         component={ReduxSelectDateRange}
      />

      Three Component Combo

      [ Select ]     [ fromDate ]     [ toDate ]

      Where the fromDate & toDate fields holds a JavaScript Date object

      <Select/>
         listens to the difference between the date
         onSelect -> sets the values to <fromDate/> and <toDate/>
 */
import lodash from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { Col, Row, Input } from 'reactstrap';
import globalDateRangeOptions from 'ui-blocks/filter-table/filterFactory/FilterForm/SelectDateRange/dateRangeOptions';
import DATES from 'constants/dates';
import {
   getDateByDayStart,
   getDateByDayEnd,
} from 'util/dateUtil';

export const TODAY = new Date();
export const YESTERDAY = ((d) => new Date(d.setDate(d.getDate() - 1)))(new Date());
export const SEVEN_DAYS_AGO = ((d) => new Date(d.setDate(d.getDate() - 7)))(new Date());
export const THIRTY_DAYS_AGO = ((d) => new Date(d.setDate(d.getDate() - 30)))(new Date());
export const FIVE_YEARS_AGO = ((d) => new Date(d.setDate(d.getDate() - 1825)))(new Date());

const MAP_INT_MONTH_TO_STRING = {
   0: 'Jan',
   1: 'Feb',
   2: 'Mar',
   3: 'Apr',
   4: 'May',
   5: 'Jun',
   6: 'Jul',
   7: 'Aug',
   8: 'Sept',
   9: 'Oct',
   10: 'Nov',
   11: 'Dec',
};

export const formatDateForAPI = (date) => {
   return `${ date.getFullYear() }-${ date.getMonth() + 1 }-${ date.getDate() }`;
};

export const NONE_DATE_OPTION = {
   value: 'NONE',
   label: 'None',
   fromDate: null,
   toDate: null,
   getDateFrom: () => null,
   getDateTo: () => null,
};

export const CUSTOM_DATE_OPTION = {
   value: 'CUSTOM',
   label: 'Custom',
   fromDate: YESTERDAY,
   toDate: TODAY,

   getDateFrom: DATES.yesterday.getDayStart,
   getDateTo: DATES.today.getDayEnd,
};

export const THIRTY_DAYS_AGO_OPTION = {
   value: 'LAST_THIRTY_DAYS',
   label: 'Last 30 Days',
   fromDate: THIRTY_DAYS_AGO,
   toDate: TODAY,

   getDateFrom: DATES.thirtyDaysAgo.getDayStart,
   getDateTo: DATES.today.getDayEnd,
};

export const SEVEN_DAYS_AGO_OPTION = {
   value: 'LAST_SEVEN_DAYS',
   label: 'Last 7 Days',
   fromDate: SEVEN_DAYS_AGO,
   toDate: TODAY,
   getDateFrom: DATES.sevenDaysAgo.getDayStart,
   getDateTo: DATES.today.getDayEnd,
};

export const legacyDateRangeOptions = [
   {
      value: 'TODAY',
      label: 'Today',
      dateFrom: DATES.today.start,
      dateTo: DATES.today.end,
      getDateFrom: DATES.today.getDayStart,
      getDateTo: DATES.today.getDayEnd,
   },
   {
      value: 'YESTERDAY',
      label: 'Yesterday',
      dateFrom: DATES.yesterday.start,
      dateTo: DATES.yesterday.end,
      getDateFrom: DATES.yesterday.getDayStart,
      getDateTo: DATES.yesterday.getDayEnd,
   },
   SEVEN_DAYS_AGO_OPTION,
   THIRTY_DAYS_AGO_OPTION,
   CUSTOM_DATE_OPTION,
];


const DATE_RANGE_OPTIONS = [...legacyDateRangeOptions, NONE_DATE_OPTION];

const baseCol = {
   select: {
      xs: 4,
      md: 3,
   },
   datePicker: {
      xs: 3,
      md: 2,
   },
};

const baseColSelectInput = {
   select: {
      xs: 4,
      // md: 3,
   },
   datePicker: {
      xs: 4,
      // md: 2
   },
};

/*
   Returns true if the two dates same of the same day of the year.
      Does different checks if the dates are instanceof Date and if they are both instanceof String
   Otherwise, returns the built-in equality for the two objects
 */
export const isSameDay = (date1, date2) => {
   if (date1 instanceof Date && date2 instanceof Date) {
      return date1.getFullYear() === date2.getFullYear()
         && date1.getMonth() === date2.getMonth()
         && date1.getDate() === date2.getDate();
   } else if (date1 instanceof String && date2 instanceof String) {
      return date1 === date2;
   } else if (
      (date1 instanceof Date && date2 instanceof String)
      || (date1 instanceof String && date2 instanceof Date)
   ) {
      const dateStr = date1 instanceof String
         ? date1
         : date2;

      const dateDate = date1 instanceof Date
         ? date1
         : date2;

      return dateStr === formatDateForAPI(dateDate);
   } else {
      return date1 === date2;
   }
};

// Returns the mm dd, yyyy for the date filter
export const getDayLabelDateRange = (date) => {
   if (!date || !(date instanceof Date)) {
      return '';
   }

   const MONTH = MAP_INT_MONTH_TO_STRING[date.getMonth()];
   const DAY = date.getDate();
   const YEAR = date.getFullYear();
   return `${ MONTH } ${ DAY }, ${ YEAR }`;
};

export const getCustomLabelDateRange = (fromDate, toDate) => {
   let dateLabel;

   if (!fromDate || !toDate) {
      dateLabel = '';
   } else if (isSameDay(fromDate, toDate)) {
      dateLabel = getDayLabelDateRange((fromDate));
   } else {
      let fromLabel = '';
      let toLabel = '';

      // Add the month
      const TO_MONTH = toDate.getMonth();
      const FROM_MONTH = fromDate.getMonth();
      fromLabel += `${ MAP_INT_MONTH_TO_STRING[FROM_MONTH] } `;
      if (FROM_MONTH !== TO_MONTH) {
         toLabel += `${ MAP_INT_MONTH_TO_STRING[TO_MONTH] } `;
      }

      // Add the date
      fromLabel += `${ fromDate.getDate() }`;
      toLabel += `${ toDate.getDate() }`;

      // Add the year
      const TO_DATE_YEAR = toDate.getFullYear();
      const FROM_DATE_YEAR = fromDate.getFullYear();
      toLabel += `, ${ TO_DATE_YEAR }`;
      if (FROM_DATE_YEAR !== TO_DATE_YEAR) {
         fromLabel += `, ${ FROM_DATE_YEAR }`;
      }

      dateLabel = `${ fromLabel } - ${ toLabel }`;
   }

   return dateLabel;
};

export const getLabelDateRange = (fromDate, toDate) => {
   const adjustedFromDate = getDateByDayStart(fromDate);
   const adjustedToDate = getDateByDayEnd(toDate);

   const possibleSelectedOption = DATE_RANGE_OPTIONS.find((option) => {
      let isMatch = false;

      if (lodash.isFunction(option?.getDateFrom) && lodash.isFunction(option?.getDateTo)) {
         const optionDateFrom = option.getDateFrom();
         const optionDateTo = option.getDateTo();

         if (
            optionDateFrom instanceof Date
            && optionDateTo instanceof Date
            && fromDate instanceof Date
            && toDate instanceof Date
         ) {
            isMatch = isSameDay(optionDateFrom, adjustedFromDate) && isSameDay(optionDateTo, adjustedToDate);
         }
      }

      return isMatch;
   });

   const SPECIFIC_DATE_LABEL = getCustomLabelDateRange(fromDate, toDate);

   let label;

   if (possibleSelectedOption) {
      label = `${ possibleSelectedOption.label }: ${ SPECIFIC_DATE_LABEL } `;
   } else if (!fromDate || !toDate) {
      label = CUSTOM_DATE_OPTION.label;
   } else {
      label = `${ CUSTOM_DATE_OPTION.label }: ${ SPECIFIC_DATE_LABEL }`;
   }

   return label;
};

const reducerNestedField = (accumulator, currentValue) => {
   const isArrayAccess = currentValue && /\w+\[{1}\d{1,}\]{1}$/.test(currentValue);
   let newValue;

   if (isArrayAccess) {
      const [arr, index] = currentValue.split(/\[|\]/)
         .filter((i) => i);
      newValue = accumulator[arr][index];
   } else {
      newValue = currentValue && currentValue in accumulator ? accumulator[currentValue] : accumulator;
   }

   return newValue;
};

const getDateRangeOptions = (hasOptionNone) => (
   hasOptionNone ? [...globalDateRangeOptions, NONE_DATE_OPTION] : globalDateRangeOptions
);

// Used for <Fields /> redux form and expects two elements of "names"
const ReduxSelectDateRange = ({ names, label, hasOptionNone, isSelectInputChild, ...props }) => {
   const [dateRangeOptions] = useState(getDateRangeOptions(hasOptionNone));

   const fromDateField = names[0].split('.')
      .reduce(reducerNestedField, props);

   const toDateField = names[1].split('.')
      .reduce(reducerNestedField, props);

   const [selectedOption, setSelectedOption] = useState({});
   const [fromDate, setFromDate] = useState(fromDateField.input.value);
   const [toDate, setToDate] = useState(toDateField.input.value);

   const toDateStr = toDate instanceof Date ? toDate?.toJSON() : '';
   const fromDateStr = fromDate instanceof Date ? fromDate?.toJSON() : '';

   // listens to the changes in fromDate and toDate
   // IF there is any change with the two of them, the SELECT option is preselected depending on their value
   useEffect(() => {

      const adjustedFromDate = getDateByDayStart(fromDate);
      const adjustedToDate = getDateByDayEnd(toDate);



      const possibleSelectedOption = dateRangeOptions.find((option) => {
         let isMatch = false;

         if (lodash.isFunction(option?.getDateFrom) && lodash.isFunction(option?.getDateTo)) {
            const optionDateFrom = option.getDateFrom();
            const optionDateTo = option.getDateTo();

            if (
               optionDateFrom instanceof Date
               && optionDateTo instanceof Date
               && fromDate instanceof Date
               && toDate instanceof Date
            ) {
               isMatch = isSameDay(optionDateFrom, adjustedFromDate) && isSameDay(optionDateTo, adjustedToDate);
            }
         }

         return isMatch;
      });

      const isSelectedOptionNew = (newVal, prevVal) => (newVal !== prevVal);

      if (
         possibleSelectedOption
      ) {
         if (isSelectedOptionNew(possibleSelectedOption.value, selectedOption.value)) {
            setSelectedOption(possibleSelectedOption);
         }
      } else if (
         hasOptionNone
         && !fromDate
         && !toDate
      ) {
         if (isSelectedOptionNew(NONE_DATE_OPTION.value, selectedOption.value)) {
            setSelectedOption(NONE_DATE_OPTION);
         }
      } else {
         setSelectedOption(CUSTOM_DATE_OPTION);
      }

      fromDateField.input.onChange(fromDate);
      toDateField.input.onChange(toDate);
   }, [fromDateStr, toDateStr]);

   const handleSelectChange = useCallback((option) => {
      setSelectedOption(option);
      if (lodash.isFunction(option?.getDateFrom) && lodash.isFunction(option?.getDateTo)) {
         setFromDate(option?.getDateFrom());
         setToDate(option?.getDateTo());
      } else {
         console.error('Error setting dateFrom and dateTo of SelectDateRange filter. Expected a function property getDateFrom and'
                       + ' getDateTo of the option that returns a date instance.');
      }
   }, []);

   if (label) {
      return (
         <Row>
            <Col xs='4' md='3' className='d-flex align-items-center'>
               { label }
            </Col>
            <Col className='mr-auto'>
               <Row>
                  <Col xs='4' md='3'>
                     <Select
                        value={ selectedOption }
                        className='react-select-container'
                        classNamePrefix='react-select'
                        options={ dateRangeOptions }
                        blurInputOnSelect={ false }
                        onChange={ handleSelectChange }
                     />
                  </Col>
                  <Col xs={ 4 } md={ 3 }>
                     <DatePicker
                        disabled={ selectedOption.value === NONE_DATE_OPTION.value }
                        selected={ fromDate }
                        onChange={ (date) => {
                           setFromDate(date);
                        } }
                        selectsStart
                        startDate={ fromDate }
                        endDate={ toDate }
                        maxDate={ toDate }
                        customInput={ <Input/> }
                        dateFormat='MM/dd/yyyy'
                     />
                  </Col>
                  <Col xs={ 4 } md={ 3 }>
                     <DatePicker
                        disabled={ selectedOption.value === NONE_DATE_OPTION.value }
                        selected={ toDate }
                        onChange={ (date) => {
                           setToDate(date);
                        } }
                        selectsEnd
                        startDate={ fromDate }
                        endDate={ toDate }
                        minDate={ fromDate }
                        customInput={ <Input/> }
                        dateFormat='MM/dd/yyyy'
                     />
                  </Col>
               </Row>
            </Col>
         </Row>
      );
   } else {
      return (
         <Row>
            <Col { ...(isSelectInputChild ? baseColSelectInput.select : baseCol.select) }>
               <Select
                  value={ selectedOption }
                  className='react-select-container'
                  classNamePrefix='react-select'
                  options={ dateRangeOptions }
                  blurInputOnSelect={ false }
                  onChange={ handleSelectChange }
               />
            </Col>
            <Col { ...(isSelectInputChild ? baseColSelectInput.datePicker : baseCol.datePicker) }>
               <DatePicker
                  disabled={ selectedOption.value === NONE_DATE_OPTION.value }
                  selected={ fromDate }
                  onChange={ (date) => {
                     setFromDate(date);
                  } }
                  selectsStart
                  startDate={ fromDate }
                  endDate={ toDate }
                  maxDate={ toDate }
                  customInput={ <Input/> }
                  dateFormat='MM/dd/yyyy'
               />
            </Col>
            <Col { ...(isSelectInputChild ? baseColSelectInput.datePicker : baseCol.datePicker) }>
               <DatePicker
                  disabled={ selectedOption.value === NONE_DATE_OPTION.value }
                  selected={ toDate }
                  onChange={ (date) => {
                     setToDate(date);
                  } }
                  selectsEnd
                  startDate={ fromDate }
                  endDate={ toDate }
                  minDate={ fromDate }
                  customInput={ <Input/> }
                  dateFormat='MM/dd/yyyy'
               />
            </Col>
         </Row>
      );
   }
};

export default ReduxSelectDateRange;
